import { Button } from '@bechtel/gds';
import styled, { css, keyframes } from 'styled-components';

export interface AIAssistantStyledProps {
  isExpanded: boolean;
}

export interface AIAssistantButtonStyledProps extends AIAssistantStyledProps {
  position: {
    bottom?: string;
    top?: string;
    left?: string;
    right?: string;
  };
  isDragging?: boolean;
}

// Slide in animation from right
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Slide out animation to right
const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Subtle pulse animation for the trigger button
const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }
  50% {
    box-shadow: 0 4px 16px rgba(0, 82, 204, 0.4);
  }
`;

export const AIAssistantContainerStyled = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  height: 100vh;
  z-index: 1200; /* Higher than nav but below modals */
  pointer-events: none; /* Allow clicks through container except on children */

  & > * {
    pointer-events: auto; /* Re-enable pointer events for children */
  }
`;

export const AIAssistantTriggerButtonStyled = styled(Button)<AIAssistantButtonStyledProps>`
  position: fixed;
  ${({ position }) => css`
    ${position.bottom && `bottom: ${position.bottom};`}
    ${position.top && `top: ${position.top};`}
    ${position.left && `left: ${position.left};`}
    ${position.right && `right: ${position.right};`}
  `}
  width: 56px;
  height: 56px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: ${({ isDragging }) => (isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)')};
  cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'pointer')};
  z-index: 1201; /* Above container */
  animation: ${({ isDragging }) =>
    isDragging
      ? 'none'
      : css`
          ${pulse} 3s ease-in-out infinite
        `};
  user-select: none;

  ${({ theme, isExpanded }) => css`
    background-image: linear-gradient(
      194deg,
      ${theme.colors.bechtel.blue[800]} 0%,
      ${theme.colors.bechtel.blue[1000]} 100%
    );
    border-color: ${theme.colors.bechtel.blue[700]};

    ${isExpanded &&
    css`
      opacity: 0;
      visibility: hidden;
      transform: scale(0.8);
    `}
  `}

  &:hover {
    transform: ${({ isDragging }) => (isDragging ? 'none' : 'scale(1.1)')};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
    animation: none; /* Stop pulse on hover */
    cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'grab')};
  }

  &:active {
    transform: scale(1);
    cursor: grabbing;
  }

  /* Mobile responsive sizing */
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 48px;
    height: 48px;
  }
`;

export const AIAssistantPanelStyled = styled.div<AIAssistantStyledProps>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100vh;
  width: 580px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Graphik',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      transform: translateX(0);
    `}

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100vw;
  }
`;

export const AIAssistantHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacings[500]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
  flex-shrink: 0;

  ${({ theme }) => css`
    background-image: linear-gradient(
      194deg,
      ${theme.colors.bechtel.blue[800]} 0%,
      ${theme.colors.bechtel.blue[1000]} 100%
    );
  `}
`;

export const AIAssistantCloseButtonStyled = styled(Button)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

export const AIAssistantIframeContainerStyled = styled.div`
  flex: 1;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.grey[100]};
  font-family:
    'Graphik',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
`;

export const AIAssistantIframeStyled = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  font-family:
    'Graphik',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
`;

export const AIAssistantLoadingStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.grey[100]};
  font-family:
    'Graphik',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
`;
